"use client";

import { MatchPlayerState } from "@/lib/multiplayer";

interface OpponentPanelProps {
  selfName: string;
  opponent: MatchPlayerState | null;
  opponentPresent: boolean;
  selfFinished: boolean;
  selfScore: number;
  // Leave the match and return to the menu (the solo run resumes there).
  onLeave: () => void;
}

// Both players' live scores during a match. On desktop it's a docked card on
// the right edge; on phones (where that card would cover the wheel) it becomes
// a slim two-up scoreboard that sits in the flow above the wheel instead.
export default function OpponentPanel({
  selfName,
  opponent,
  opponentPresent,
  selfFinished,
  selfScore,
  onLeave,
}: OpponentPanelProps) {
  const bothFinished = selfFinished && !!opponent?.finished;

  let verdict: { label: string; className: string } | null = null;
  if (bothFinished && opponent) {
    if (selfScore > opponent.score) {
      verdict = {
        label: "🏆 You win!",
        className: "border-green-400/60 text-green-300",
      };
    } else if (selfScore < opponent.score) {
      verdict = {
        label: "Defeated",
        className: "border-red-500/60 text-red-300",
      };
    } else {
      verdict = {
        label: "It's a tie!",
        className: "border-yellow-400/60 text-yellow-300",
      };
    }
  }

  return (
    <div className="z-30 mx-auto mt-2 w-[min(96vw,30rem)] md:fixed md:right-4 md:top-1/2 md:mt-0 md:w-[min(70vw,15rem)] md:-translate-y-1/2">
      {/* Mobile: slim two-up scoreboard — never overlaps the wheel. */}
      <div className="rounded-2xl border border-white/15 bg-background/85 p-2 shadow-[0_0_20px_rgba(0,0,0,0.4)] backdrop-blur md:hidden">
        <div className="flex items-stretch gap-2 text-center">
          <div className="flex-1 rounded-lg border border-accent/30 bg-accent/10 px-2 py-1">
            <div className="flex items-center justify-center gap-1 text-[0.5rem] font-bold uppercase tracking-widest text-accent">
              You
              {selfFinished && <span className="text-green-400">· done</span>}
            </div>
            <div className="font-mono text-base font-black leading-tight text-white">
              {selfScore.toLocaleString()}
            </div>
          </div>
          <div className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1">
            <div className="flex items-center justify-center gap-1 text-[0.5rem] font-bold uppercase tracking-widest text-white/60">
              <span className="truncate">{opponent?.name ?? "…"}</span>
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                  opponentPresent ? "bg-green-400" : "bg-white/30"
                }`}
              />
            </div>
            <div className="font-mono text-base font-black leading-tight text-accent">
              {(opponent?.score ?? 0).toLocaleString()}
            </div>
          </div>
        </div>
        {verdict ? (
          <div
            className={`mt-1.5 rounded-lg border px-2 py-1 text-center text-xs font-bold uppercase tracking-widest ${verdict.className}`}
          >
            {verdict.label}
          </div>
        ) : selfFinished && opponent && !opponent.finished ? (
          <div className="mt-1.5 rounded-lg border border-accent/30 bg-accent/10 px-2 py-1 text-center text-[0.6rem] text-accent">
            You finished — spectating {opponent.name}…
          </div>
        ) : null}
        <button
          onClick={onLeave}
          className="mt-1.5 w-full text-center text-[0.6rem] font-bold uppercase tracking-widest text-white/40 transition hover:text-white/70"
        >
          Leave Match
        </button>
      </div>

      {/* Desktop: the full vertical card on the right edge. */}
      <div className="hidden rounded-2xl border border-white/15 bg-background/85 p-4 shadow-[0_0_30px_rgba(0,0,0,0.5)] backdrop-blur md:block">
        {/* You */}
        <div className="mb-3 rounded-lg border border-accent/30 bg-accent/10 px-3 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-xs font-bold uppercase tracking-widest text-accent">
              You · {selfName}
            </span>
            {selfFinished && (
              <span className="shrink-0 text-[0.55rem] uppercase tracking-widest text-green-400">
                Done
              </span>
            )}
          </div>
          <div className="font-mono text-2xl font-black tracking-tight text-white">
            {selfScore.toLocaleString()}
          </div>
        </div>

        <div className="mb-2 text-[0.65rem] font-bold uppercase tracking-widest text-white/40">
          Playing vs
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-base font-extrabold text-white">
            {opponent?.name ?? "…"}
          </span>
          {opponentPresent ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-[0.6rem] uppercase tracking-widest text-green-400">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
              Online
            </span>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-1 text-[0.6rem] uppercase tracking-widest text-white/30">
              <span className="h-1.5 w-1.5 rounded-full bg-white/30" />
              Offline
            </span>
          )}
        </div>

        {opponent ? (
          <>
            <div className="mt-3 font-mono text-3xl font-black tracking-tight text-accent">
              {opponent.score.toLocaleString()}
            </div>
            <div className="text-xs font-semibold uppercase tracking-widest text-white/50">
              {opponent.rank}
            </div>

            <div className="mt-3 space-y-1 text-xs text-white/50">
              {opponent.alignmentLabel && (
                <div>
                  Alignment:{" "}
                  <span className="text-white/80">{opponent.alignmentLabel}</span>
                </div>
              )}
              <div>
                {opponent.finished
                  ? "Finished — final score in"
                  : opponent.totalWheels > 0
                    ? `Wheel ${opponent.wheelNumber} of ${opponent.totalWheels}`
                    : "Spinning…"}
              </div>
              {opponent.lastResult && (
                <div className="truncate">
                  Last:{" "}
                  <span className="text-white/80">
                    {opponent.lastResult.name}
                  </span>{" "}
                  <span
                    className={
                      opponent.lastResult.value >= 0
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {opponent.lastResult.value >= 0 ? "+" : ""}
                    {opponent.lastResult.value}
                  </span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-3 text-xs text-white/40">
            {opponentPresent
              ? "Connecting…"
              : "Waiting for your friend to join."}
          </div>
        )}

        {selfFinished && opponent && !opponent.finished && (
          <div className="mt-3 rounded-lg border border-accent/30 bg-accent/10 px-3 py-2 text-xs text-accent">
            You finished! Spectating your friend…
          </div>
        )}

        {verdict && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-center text-sm font-bold uppercase tracking-widest ${verdict.className}`}
          >
            {verdict.label}
            <div className="mt-1 text-[0.65rem] font-normal tracking-normal text-white/50">
              {selfScore.toLocaleString()} vs {opponent?.score.toLocaleString()}
            </div>
          </div>
        )}

        <button
          onClick={onLeave}
          className="mt-3 w-full rounded-lg border border-white/15 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-white/50 transition hover:border-white/40 hover:text-white/80"
        >
          Leave Match
        </button>
      </div>
    </div>
  );
}
